import * as React from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { Theme } from "@jelly/ui";

import defaultStyle from "./LessonItemNavigator.module.scss";
import { Slide, useUi, SlideParser } from "@jelly/lesson";
import { TaskState } from "@jelly/lesson/dist/@types/components/Task";
import { useIntl } from "react-intl";

export interface LessonItemNavigatorProps {
  items: Slide[];
  active: number;
  nextLessonUrl: string | null;
  prevLessonUrl: string | null;
  state?: TaskState;
  onChangeActive: (newSlideIndex: number) => void;
  onCheckAnswer?: () => Promise<void>;
  onRestoreTask?: () => void;
  style?: Record<string, string>;
  hideBreadcrumbs?: boolean;
}

interface ButtonTemplateConfig {
  text?: string;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  url?: string | null;
  theme?: Theme;
  icon?: string;
  iconPlacement?: "left" | "right";
}

export const LessonItemNavigator: React.FC<LessonItemNavigatorProps> = (props) => {
  const { Button, Popover } = useUi();

  const { items, active, onChangeActive, onCheckAnswer, style = defaultStyle, hideBreadcrumbs = false } = props;
  const intl = useIntl();

  const activeItem = items[active];

  const prevItem = items[active - 1];

  const nextItem = items[active + 1];

  const isActiveTask = React.useMemo(() => SlideParser.haveTask(activeItem), [activeItem]);
  const isPrevTask = React.useMemo(() => (prevItem ? SlideParser.haveTask(prevItem) : false), [prevItem]);
  const isNextTask = React.useMemo(() => (nextItem ? SlideParser.haveTask(nextItem) : false), [nextItem]);

  const defaultOnPrev = (): void => {
    onChangeActive(active - 1);
  };
  const defaultOnNext = (): void => {
    onChangeActive(active + 1);
  };

  function restoreTask(): void {
    if (props.onRestoreTask) {
      props.onRestoreTask();
    }
  }

  function getSubText(index: number): string {
    return `${index + 1} / ${items.length}`;
  }

  function renderPrevButtonTemplate(data: ButtonTemplateConfig): React.ReactNode {
    const { icon = "cil-arrow-left", iconPlacement = "left", theme = "default" } = data;

    const button = (
      <Button
        shape="round"
        theme={theme}
        icon={icon}
        iconPlacement={iconPlacement}
        onClick={data.onClick}
        disabled={data.disabled}
        className={data.className}
      >
        {data.text}
      </Button>
    );

    return data.url ? <Link to={data.url}>{button}</Link> : button;
  }

  function renderNextButtonTemplate(data: ButtonTemplateConfig): React.ReactNode {
    const { icon = "cil-arrow-right", iconPlacement = "right", theme = "default" } = data;

    const button = (
      <Button
        shape="round"
        theme={theme}
        icon={icon}
        iconPlacement={iconPlacement}
        disabled={data.disabled}
        className={data.className}
        onClick={data.onClick}
      >
        {data.text}
      </Button>
    );
    const buttonRestoreTask =
      isActiveTask && props.state?.type !== "initial" ? (
        <Button shape="round" theme={theme} icon="cis-restore" className={style.restore} onClick={() => restoreTask()}>
          {intl.formatMessage({ id: "lessonItemNavigator.restoreTask", defaultMessage: "Restore Task" })}
        </Button>
      ) : null;

    return (
      <>
        {buttonRestoreTask}
        {data.url ? <Link to={data.url}>{button}</Link> : button}
      </>
    );
  }

  function renderNextButton(): React.ReactNode {
    if (isActiveTask && onCheckAnswer) {
      if (props.state?.type !== "checked" && props.state?.type !== "initial") {
        return renderNextButtonTemplate({
          text: intl.formatMessage({ id: "lessonItemNavigator.checkAnswer", defaultMessage: "Check your answer" }),

          className: "check-answer",
          onClick: () => onCheckAnswer(),
          icon: "cis-check",
          iconPlacement: "left",
        });
      }
      if (!nextItem) {
        return renderNextButtonTemplate({
          text: intl.formatMessage({ id: "lessonItemNavigator.nextLesson", defaultMessage: "Next lesson" }),
          className: "next-lesson",
          disabled: props.nextLessonUrl === null,
          url: props.nextLessonUrl,
        });
      }

      return renderNextButtonTemplate({
        onClick: defaultOnNext,
      });
    }

    if (!nextItem) {
      //return <></>;
      // Orignal implementation:
      return renderNextButtonTemplate({
        text: intl.formatMessage({ id: "lessonItemNavigator.nextLesson", defaultMessage: "Next lesson" }),
        className: "next-lesson",
        disabled: props.nextLessonUrl === null,
        url: props.nextLessonUrl,
      });
    }

    if (isNextTask) {
      return renderNextButtonTemplate({
        text: intl.formatMessage({ id: "lessonItemNavigator.solveTask", defaultMessage: "Solve a task" }),
        className: "solve-task",
        onClick: defaultOnNext,
        icon: "cis-check",
      });
    }

    return renderNextButtonTemplate({
      onClick: defaultOnNext,
    });
  }

  function renderPrevButton(): React.ReactNode {
    if (!prevItem) {
      return renderPrevButtonTemplate({
        text: intl.formatMessage({ id: "lessonItemNavigator.prevLesson", defaultMessage: "Previous lesson" }),
        className: "prev-lesson",
        disabled: props.prevLessonUrl === null,
        url: props.prevLessonUrl,
      });
    }

    if (isActiveTask) {
      if (!isPrevTask) {
        return renderPrevButtonTemplate({
          text: intl.formatMessage({ id: "lessonItemNavigator.backToPrinciple", defaultMessage: "Back to principle" }),
          className: "back-top-principle",
          onClick: defaultOnPrev,
        });
      }

      return renderPrevButtonTemplate({
        onClick: defaultOnPrev,
      });
    }

    if (!isActiveTask) {
      if (isPrevTask) {
        return renderPrevButtonTemplate({
          text: intl.formatMessage({ id: "lessonItemNavigator.backToTask", defaultMessage: "Back to task" }),
          className: "back-top-task",
          onClick: defaultOnPrev,
        });
      }

      return renderPrevButtonTemplate({
        onClick: defaultOnPrev,
      });
    }

    return null;
  }

  return (
    <div className={style.navbar}>
      <div className={classnames(style.buttonContainer, style.left)}>{renderPrevButton()}</div>
      <div className={classnames(style.breadcrumbs, { [style.hidden]: hideBreadcrumbs })}>
        {items.map((_, index) => (
          <Popover key={index} subTxt={getSubText(index)}>
            <div
              className={classnames(style.dot, { [style.active]: index === active })}
              onClick={() => onChangeActive(index)}
            />
          </Popover>
        ))}
      </div>
      <div className={classnames(style.buttonContainer, style.right)}>{renderNextButton()}</div>
    </div>
  );
};

export default LessonItemNavigator;
